/**
 * Searchers feature — public contract.
 *
 * Owns the real, typed search forms rendered inside the branding hero's search
 * card. Nothing here imports legacy search UI: the legacy header keeps using
 * `legacyfrontend/components/home/components/SearchSelector.jsx` untouched for
 * agencies that have not published personalization yet.
 */
import type { ComponentType } from 'react';

/** Searcher ids that have a real, working form behind them. */
export const REAL_SEARCHER_IDS = ['paquetes', 'vuelos', 'hoteles'] as const;

export type RealSearcherId = (typeof REAL_SEARCHER_IDS)[number];

export function isRealSearcherId(id: string): id is RealSearcherId {
  return (REAL_SEARCHER_IDS as readonly string[]).includes(id);
}

/**
 * Ids the registry has a form for. Declared here — not derived from
 * `registry.ts` — so callers that only need to know *whether* a searcher is
 * offered (e.g. `mapSettingsToBrandingProps`) never pull the form components
 * and their dependencies into a non-browser bundle. `registry.test.ts` keeps
 * both in sync.
 */
export const SEARCHER_IDS_WITH_FORM: readonly RealSearcherId[] = ['paquetes', 'vuelos', 'hoteles'];

export function hasSearcherForm(id: string): boolean {
  return (SEARCHER_IDS_WITH_FORM as readonly string[]).includes(id);
}

export interface SearcherFormProps {
  /** Renders the compact, single-column layout used inside mobile dialogs. */
  isMobile?: boolean;
  /** Fired right before navigating to the results route. */
  onSearchStart?: () => void;
  /** Fired when a submit attempt finished, successfully or not. */
  onSearchComplete?: () => void;
}

export type SearcherFormComponent = ComponentType<SearcherFormProps>;
